<template>
  <div
    class="relative flex flex-col min-w-0 break-words bg-white w-full mb-6 shadow-lg rounded"
  >
    <div class="rounded-t mb-0 px-4 py-3 bg-transparent">
      <div class="flex flex-wrap items-center">
        <div class="relative w-full max-w-full flex-grow flex-1">
          <h6 class="uppercase text-blueGray-400 mb-1 text-xs font-semibold">
            Performance
          </h6>
          <h2 class="text-blueGray-700 text-xl font-semibold">
            支付订单数量
          </h2>
        </div>
      </div>
    </div>
    <div class="p-4 flex-auto">
      <div class="relative h-350-px">
        <canvas id="bar-chart"></canvas>
      </div>
    </div>
  </div>
</template>
<script>
import { Chart, registerables } from "chart.js";
import { getAdminApiUrl } from "@/api/luckdraw.js";
import { getUserToken } from "@/utils/auth.js";

// 注册所有Chart.js组件
Chart.register(...registerables);

// 生成模拟数据
function generateHourlyData(baseValue, variance) {
  const data = [];
  for (let i = 0; i < 24; i++) {
    let hourlyFactor = 1;
    if (i >= 9 && i <= 21) {
      hourlyFactor = 1.5 + Math.random() * 0.5;
    } else if (i >= 6 && i < 9) {
      hourlyFactor = 1.2 + Math.random() * 0.3;
    } else if (i > 21 && i <= 23) {
      hourlyFactor = 0.8 + Math.random() * 0.3;
    } else {
      hourlyFactor = 0.2 + Math.random() * 0.3;
    }
    const value = Math.floor(baseValue * hourlyFactor + (Math.random() - 0.5) * variance);
    data.push(Math.max(0, value));
  }
  return data;
}

function getTodayString() {
  const today = new Date();
  return `${today.getMonth() + 1}月${today.getDate()}日`;
}

function getYesterdayString() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return `${yesterday.getMonth() + 1}月${yesterday.getDate()}日`;
}

function getSevenDaysAgoString() {
  const date = new Date();
  date.setDate(date.getDate() - 7);
  return `${date.getMonth() + 1}月${date.getDate()}日`;
}

// 从API获取数据
async function fetchHourlyStats() {
  const token = getUserToken();
  const response = await fetch(getAdminApiUrl('/stat/payment/hourly'), {
    method: 'POST',
    headers: {
      'Token': `${token}`,
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    },
    credentials: 'include',
    body: JSON.stringify({ days: 3 })
  });
  const result = await response.json();
  if (result.sta === 0 && result.data) {
    return result.data;
  }
  return null;
}

export default {
  mounted: function () {
    this.$nextTick(async function () {
      const apiData = await fetchHourlyStats();

      let config;

      if (apiData && apiData.datasets && apiData.datasets.length > 0) {
        config = {
          type: "line",
          data: {
            labels: apiData.hours,
            datasets: apiData.datasets.map(ds => ({
              label: ds.label,
              backgroundColor: ds.color,
              borderColor: ds.color,
              data: ds.data,
              fill: false,
              tension: 0.3,
            }))
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "rgba(0,0,0,.4)" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + context.parsed.y + ' 单';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                display: true,
                title: { display: true, text: "时间" },
                grid: {
                  borderDash: [2],
                  borderDashOffset: 2,
                  color: "rgba(33, 37, 41, 0.3)",
                  zeroLineColor: "rgba(33, 37, 41, 0.3)",
                  zeroLineBorderDash: [2],
                  zeroLineBorderDashOffset: 2,
                },
              },
              y: {
                display: true,
                title: { display: true, text: "订单数量" },
                grid: {
                  borderDash: [2],
                  drawBorder: false,
                  borderDashOffset: 2,
                  color: "rgba(33, 37, 41, 0.2)",
                  zeroLineColor: "rgba(33, 37, 41, 0.15)",
                  zeroLineBorderDash: [2],
                  zeroLineBorderDashOffset: 2,
                },
                ticks: {
                  callback: function(value) { return value + ' 单'; }
                }
              },
            },
          },
        };
      } else {
        // 模拟数据
        const todayData = generateHourlyData(30, 20);
        const yesterdayData = generateHourlyData(25, 18);
        const sevenDaysAgoData = generateHourlyData(20, 15);
        const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);

        config = {
          type: "line",
          data: {
            labels: hours,
            datasets: [
              {
                label: getTodayString(),
                backgroundColor: "#10b981",
                borderColor: "#10b981",
                data: todayData,
                fill: false,
                tension: 0.3,
              },
              {
                label: getYesterdayString(),
                backgroundColor: "#3b82f6",
                borderColor: "#3b82f6",
                data: yesterdayData,
                tension: 0.3,
              },
              {
                label: getSevenDaysAgoString(),
                backgroundColor: "#f59e0b",
                borderColor: "#f59e0b",
                data: sevenDaysAgoData,
                tension: 0.3,
              },
            ],
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: {
                labels: { color: "rgba(0,0,0,.4)" },
                align: "end",
                position: "bottom",
              },
              tooltip: {
                mode: "index",
                intersect: false,
                callbacks: {
                  label: function(context) {
                    return context.dataset.label + ': ' + context.parsed.y + ' 单';
                  }
                }
              },
            },
            interaction: {
              mode: "nearest",
              intersect: true,
            },
            scales: {
              x: {
                display: true,
                title: { display: true, text: "时间" },
                grid: {
                  borderDash: [2],
                  borderDashOffset: 2,
                  color: "rgba(33, 37, 41, 0.3)",
                  zeroLineColor: "rgba(33, 37, 41, 0.3)",
                  zeroLineBorderDash: [2],
                  zeroLineBorderDashOffset: 2,
                },
              },
              y: {
                display: true,
                title: { display: true, text: "订单数量" },
                grid: {
                  borderDash: [2],
                  drawBorder: false,
                  borderDashOffset: 2,
                  color: "rgba(33, 37, 41, 0.2)",
                  zeroLineColor: "rgba(33, 37, 41, 0.15)",
                  zeroLineBorderDash: [2],
                  zeroLineBorderDashOffset: 2,
                },
                ticks: {
                  callback: function(value) { return value + ' 单'; }
                }
              },
            },
          },
        };
      }

      let ctx = document.getElementById("bar-chart").getContext("2d");
      window.myBar = new Chart(ctx, config);
    });
  },
};
</script>
